import { defaultTo, orderBy } from 'lodash';
import { DateTime } from 'luxon';
import { VisitSalesItemPublicModel } from './VisitSalesItemPublicModel';
import {
  IVisitSalesItemAttributeModel,
  IVisitSalesItemPublicModel,
  VisitSalesItemAttributeModel,
  VisitSalesItemTraitListingModel,
  IVisitSalesItemTraitListingModel,
} from '.';
import { computed, makeObservable } from 'mobx';
import { DiscountItemTypes, SalesItemType, Traits } from './Enums';

export interface IVisitSalesItemModel extends IVisitSalesItemPublicModel {
  isInPackage: boolean;
  salesItemId: number;
  salesItemOptionId: number;
  salesPlanId: number | undefined;
  salesPlanName: string | undefined;
  salesItemSalesItemTypeId: number;
  salesItemSalesItemTypeName: string;
  salesItemCommunicationTypeId: number | null;
  salesItemReminderDays: number | null;
  dueDate?: string | undefined;
  salesItemDiscountTypeName: string;
  salesItemSalesCategoryName: string;
  postRebatePrice?: number;
  invoicePrice: number;
  salesItemSupplierProductCode: string;
  visitId: number;
  visitSalesItemId: number;
  parentVisitSalesItemId: number | undefined;
  salesItemAutoAddToVisitTypeId: number;
  visitSalesItemAttributes: IVisitSalesItemAttributeModel[];
  salesItemOptionBusinessLineSKU: string | undefined;
  salesItemOptionPetcoSKU: string | undefined;
  salesItemOptionUnitOfMeasureQuantity: number | undefined;
  salesItemOptionUnitOfMeasureId: number | undefined;
  salesItemOptionUnitOfMeasureName: string | undefined;
  visitSalesItemTraits: IVisitSalesItemTraitListingModel[];
  requiresAction: (salesItemSalesItemTypeId: number) => boolean; // Definite assignment assertion
  actionTaken: (
    requiresAction: boolean,
    isPending: boolean,
    given: boolean,
    wasDeclinedByVeterinarian: boolean,
    wasDeclinedByClient: boolean,
  ) => boolean;
  diagnosticHTML?: string;
}

export class VisitSalesItemModel
  extends VisitSalesItemPublicModel
  implements IVisitSalesItemModel
{
  visitId: number;
  visitSalesItemId: number;
  salesItemId: number;
  salesItemOptionId: number;
  visitSalesItemAttributes: VisitSalesItemAttributeModel[];
  parentVisitSalesItemId: number | undefined;
  salesItemOptionBusinessLineSKU: string | undefined;
  attributeElements: (string | JSX.Element | JSX.Element[])[];
  isInPackage: boolean;
  salesPlanId: number | undefined;
  salesPlanName: string | undefined;
  salesItemSalesItemTypeId: number;
  salesItemSalesItemTypeName: string;
  salesItemCommunicationTypeId: number | null;
  salesItemOptionPetcoSKU: string | undefined;
  salesItemOptionUnitOfMeasureQuantity: number | undefined;
  salesItemOptionUnitOfMeasureId: number | undefined;
  salesItemOptionUnitOfMeasureName: string | undefined;
  salesItemSupplierProductCode: string;
  visitSalesItemTraits: VisitSalesItemTraitListingModel[];
  salesItemReminderDays: number | null;
  salesItemAutoAddToVisitTypeId: number;
  dueDate?: string | undefined;
  diagnosticHTML?: string;
  requiresAction!: (salesItemSalesItemTypeId: number) => boolean;
  actionTaken!: (
    isPending: boolean,
    given: boolean,
    wasDeclinedByVeterinarian: boolean,
    wasDeclinedByClient: boolean,
  ) => boolean;
  salesItemDiscountTypeName: any;
  salesItemSalesCategoryName: string;

  constructor(params: IVisitSalesItemModel, visitDate?: DateTime | undefined) {
    super(params);
    this.visitId = defaultTo(params.visitId, 0);
    this.visitSalesItemId = defaultTo(params.visitSalesItemId, 0);
    this.salesItemId = defaultTo(params.salesItemId, 0);
    this.salesItemOptionId = defaultTo(params.salesItemOptionId, 0);
    this.salesItemSalesItemTypeId = defaultTo(
      params.salesItemSalesItemTypeId,
      0,
    );
    this.parentVisitSalesItemId = params.parentVisitSalesItemId;
    this.salesItemAutoAddToVisitTypeId = defaultTo(
      params.salesItemAutoAddToVisitTypeId,
      0,
    );
    this.salesItemOptionBusinessLineSKU = params.salesItemOptionBusinessLineSKU;
    this.salesItemOptionPetcoSKU = defaultTo(
      params.salesItemOptionPetcoSKU,
      '',
    );
    this.salesItemOptionUnitOfMeasureQuantity =
      params.salesItemOptionUnitOfMeasureQuantity;
    this.salesItemOptionUnitOfMeasureId = params.salesItemOptionUnitOfMeasureId;
    this.salesItemOptionUnitOfMeasureName = defaultTo(
      params.salesItemOptionUnitOfMeasureName,
      '',
    );
    this.salesItemCommunicationTypeId = defaultTo(
      params.salesItemCommunicationTypeId,
      0,
    );
    this.salesItemSalesCategoryName = defaultTo(
      params.salesItemSalesCategoryName,
      '',
    );
    this.invoicePrice = defaultTo(params.invoicePrice, 0);
    this.salesItemSalesItemTypeName = defaultTo(
      params.salesItemSalesItemTypeName,
      '',
    );
    this.visitSalesItemAttributes = orderBy(
      params.visitSalesItemAttributes ?? [],
      'sortOrder',
    ).map(
      (vsa) =>
        new VisitSalesItemAttributeModel(
          vsa,
          visitDate,
          this.quantity,
          this.salesItemOptionUnitOfMeasureName,
          this.salesItemCommunicationTypeId!,
        ),
    );
    this.isInPackage = defaultTo(params.isInPackage, false);
    this.salesPackageName = params.salesPackageName;
    this.salesPlanId = params.salesPlanId;
    this.salesPlanName = params.salesPlanName;
    this.attributeElements =
      VisitSalesItemAttributeModel.parseAttributeElements(
        this.visitSalesItemAttributes,
      );
    this.salesItemSalesItemTypeId = defaultTo(
      params.salesItemSalesItemTypeId,
      0,
    );
    this.salesItemSupplierProductCode = params.salesItemSupplierProductCode;
    this.visitSalesItemTraits = (params.visitSalesItemTraits ?? []).map(
      (vsit) => new VisitSalesItemTraitListingModel(vsit),
    );
    this.salesItemReminderDays = params.salesItemReminderDays;
    this.diagnosticHTML = params.diagnosticHTML ?? undefined;
    this.dueDate = defaultTo(
      params.dueDate,
      !_.isNil(params.salesItemReminderDays) && !_.isNil(visitDate)
        ? visitDate.plus({ days: params.salesItemReminderDays! }).toISO()
        : undefined,
    );
    // This is for the validation on the service and product details screen.
    this.requiresAction = (salesItemSalesItemTypeId: number) => {
      switch (salesItemSalesItemTypeId) {
        case 1:
          SalesItemType.ClinicService;
        case 2:
          SalesItemType.PrescriptionProduct;
        case 3:
          SalesItemType.LicenseFee;
        case 4:
          SalesItemType.VetcoPrescription;
          return true;
        case 5:
          SalesItemType.CouponDiscountMonetary;
        case 6:
          SalesItemType.CouponDiscountPercentage;
        case 7:
          SalesItemType.RebateProducts;
        case 8:
          SalesItemType.RebateServices;
        case 9:
          SalesItemType.FeeNonTaxable;
        case 10:
          SalesItemType.FeeTaxable;
          return false;
        default:
          return true;
      }
    };
    // This is for the validation on the service and product details screen.
    this.actionTaken = (
      isPending: boolean,
      given: boolean,
      wasDeclinedByVeterinarian: boolean,
      wasDeclinedByClient: boolean,
    ) => {
      return (
        isPending || given || wasDeclinedByVeterinarian || wasDeclinedByClient
      );
    };
    this.dueDate = defaultTo(
      params.dueDate,
      !_.isNil(params.salesItemReminderDays) && !_.isNil(visitDate)
        ? visitDate.plus({ days: params.salesItemReminderDays! }).toISO()
        : undefined,
    );

    makeObservable(this);
  }

  isAutoAddedToVisit() {
    return this.salesItemAutoAddToVisitTypeId !== 1;
  }

  @computed get IsDiscountItem() {
    return DiscountItemTypes.includes(this.salesItemSalesItemTypeId);
  }

  @computed
  get hasLoweredPrice(): boolean {
    return (
      this.invoicePrice != null && this.invoicePrice < this.salesItemPrice
    );
  }

  @computed
  get invoicePriceOrSalesPrice(): number {
    return this.invoicePrice ?? this.salesItemPrice;
  }

  @computed
  get isAllowedToRemove() {
    if (!this.visitSalesItemTraits) return true;
    const hasDisallowRemovalFromVisit = this.visitSalesItemTraits.some(
      (t) => t.traitId === Traits.DisallowRemovalFromVisit,
    );
    return !hasDisallowRemovalFromVisit;
  }

  @computed
  get isAllowedToDecline() {
    if (this.parentVisitSalesItemId) {
      return false;
    }
    const hasAllowDecline = this.visitSalesItemTraits.some(
      (t) => t.traitId === Traits.AllowDecline,
    );
    return hasAllowDecline;
  }

  fullDisplayName(): string {
    let result = this.salesItemFullName;

    if (this.salesItemOptionDisplayName) {
      result += ' ' + this.salesItemOptionDisplayName;
    }

    return result;
  }

  nameModifier(): string {
    let result = '';

    if (this.isPending) {
      result += ' (pending)';
    }

    if (this.wasDeclinedByClient) {
      result += ' (declined)';
    }

    return result;
  }
}
